import { useCallback, useMemo } from 'react';
import { switchcase, useNoticeBannersStackContext } from '@sweetgreen/garnish';

import type { SubscriptionCreditCard } from '@order/graphql';
import { useAddSubscriptionPaymentMethodMutation } from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

export const useSweetpassPaymentMethodAdd = () => {
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const isSweetpassEnabled = useFeatureFlag('OPS-sweetpass-enabled', {
    listenForChanges: true,
  });

  const [addPaymentMethodResponse, addPaymentMethodMutation] =
    useAddSubscriptionPaymentMethodMutation();

  const addPaymentMethod = useCallback(
    async (nonce: string) => {
      const { data, error } = await addPaymentMethodMutation({
        nonce,
      });

      const responseType = data?.addSubscriptionPaymentMethod.__typename;

      if (responseType === 'AddSubscriptionPaymentMethodSuccessResponse') {
        return data?.addSubscriptionPaymentMethod.paymentMethodToken;
      }

      if (!responseType || error) {
        addNoticeBanner({
          text: t('general.error'),
          palette: 'caution',
        });

        return;
      }

      const userError = switchcase({
        PaymentMethodAlreadyExistsError: t(
          'sweetpass.payment-method.duplicate',
        ),
        UnknownUserError: t('general.error'),
      })(t('general.error'))(responseType);

      addNoticeBanner({
        text: userError,
        palette: 'caution',
      });
    },
    [addPaymentMethodMutation, addNoticeBanner, t],
  );

  return useMemo(
    () => ({
      addPaymentMethod,
      isSweetpassEnabled,
      isLoading: addPaymentMethodResponse.fetching,
    }),
    [addPaymentMethod, isSweetpassEnabled, addPaymentMethodResponse.fetching],
  );
};

export type UseSweetpassPaymentMethodEdit = {
  readonly paymentMethodId: string;
};

export type PaymentMethod = NonNullable<SubscriptionCreditCard> | undefined;
